import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, FolderKanban, CheckSquare, MessageSquare, 
  Bot, BarChart3, Settings, LogOut, Sparkles, Menu, X, ChevronLeft, Users, Building2,
  Shield, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { useAuthStore, useUIStore, useProjectStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { VoiceOverlay, VoiceMicButton } from '../components/VoiceOverlay';
import { Toaster } from '../components/ui/sonner';

const AppLayout = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const { language, sidebarOpen, toggleSidebar } = useUIStore();
  const { currentProject, setCurrentProject } = useProjectStore();
  const { t } = useTranslation(language);

  const navItems = [
    { path: '/app', icon: LayoutDashboard, label: t('dashboard'), exact: true },
    { path: '/app/projects', icon: FolderKanban, label: t('projects') },
    { path: '/app/tasks', icon: CheckSquare, label: t('tasks') },
    { path: '/app/chat', icon: MessageSquare, label: t('chat') },
    { path: '/app/agents', icon: Bot, label: t('agents') },
    { path: '/app/reports', icon: BarChart3, label: t('reports') },
    { path: '/app/departments', icon: Building2, label: language === 'hi' ? 'विभाग' : 'Departments' },
    { path: '/app/teams', icon: Users, label: t('teams') },
    { path: '/app/approvals', icon: Clock, label: language === 'hi' ? 'अनुमोदन' : 'Approvals' },
  ];

  // Admin-only nav items
  const adminItems = user?.is_admin ? [
    { path: '/app/admin', icon: Shield, label: 'Admin', isAdmin: true },
  ] : [];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="h-screen flex overflow-hidden bg-background" data-testid="app-layout">
      {/* Sidebar */}
      <motion.aside
        initial={{ width: sidebarOpen ? 256 : 0 }}
        animate={{ width: sidebarOpen ? 256 : 0 }}
        transition={{ duration: 0.3 }}
        className="h-full border-r border-white/10 bg-card/50 backdrop-blur-xl flex flex-col overflow-hidden"
      >
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-6 w-6 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-lg whitespace-nowrap">OSLO One</span>
          </div>
        </div>

        <ScrollArea className="flex-1 p-3">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    isActive
                      ? 'bg-primary/20 text-primary'
                      : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
                  }`
                }
                data-testid={`nav-${item.path.replace('/app/', '') || 'dashboard'}`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="whitespace-nowrap">{item.label}</span>
              </NavLink>
            ))}
            
            {/* Admin Items */}
            {adminItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    isActive
                      ? 'bg-destructive/20 text-destructive'
                      : 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
                  }`
                }
                data-testid="nav-admin"
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="whitespace-nowrap">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Current Project */}
          {currentProject && (
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="text-xs text-muted-foreground uppercase tracking-wider px-3 mb-2">
                {language === 'hi' ? 'वर्तमान प्रोजेक्ट' : 'Current Project'}
              </p>
              <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-secondary/30">
                <div className="flex items-center gap-2 min-w-0">
                  <FolderKanban className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm truncate">{currentProject.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 flex-shrink-0"
                  onClick={() => setCurrentProject(null)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        </ScrollArea>

        <div className="p-3 border-t border-white/10 space-y-1">
          <NavLink
            to="/app/settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                isActive
                  ? 'bg-primary/20 text-primary'
                  : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
              }`
            }
            data-testid="nav-settings"
          >
            <Settings className="h-5 w-5" />
            <span>{t('settings')}</span>
          </NavLink>
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-muted-foreground hover:bg-destructive/20 hover:text-destructive transition-all"
            data-testid="nav-logout"
          >
            <LogOut className="h-5 w-5" />
            <span>{t('logout')}</span>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-14 border-b border-white/10 flex items-center justify-between px-4 bg-card/30 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="hover:bg-secondary/50"
              data-testid="toggle-sidebar-btn"
            >
              {sidebarOpen ? <ChevronLeft className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/50">
              <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                <span className="text-xs font-semibold text-primary-foreground">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-sm font-medium">{user?.name}</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* Voice Components */}
      <VoiceOverlay />
      <VoiceMicButton />
      
      {/* Toast Notifications */}
      <Toaster richColors position="top-right" />
    </div>
  );
};

export default AppLayout;
